import type { ModelCompatibilityFindingOutputV1 } from '@seeqdev/api/model/ModelCompatibilityFindingOutputV1';

/**
 * Maps each backend `CompatibilityCheck` reasonCode to its `en.json` translation key.
 *
 * To add a reason code: add an entry here and the matching string in `en.json`. A
 * contract test in this module's spec fails if any entry lacks its translation.
 * If the server emits a reasonCode not in this map,
 * [isKnownModelCompatibilityFinding] filters the finding out before render.
 */
export const REASON_CODE_KEYS = {
  OUTPUT_FEATURE_TYPE_CHANGED: 'MODELS.COMPATIBILITY_FINDING_OUTPUT_FEATURE_TYPE_CHANGED',
  OUTPUT_FEATURE_DELETED: 'MODELS.COMPATIBILITY_FINDING_OUTPUT_FEATURE_DELETED',
  OUTPUT_FEATURE_ADDED: 'MODELS.COMPATIBILITY_FINDING_OUTPUT_FEATURE_ADDED',
  INPUT_FEATURE_TYPE_CHANGED: 'MODELS.COMPATIBILITY_FINDING_INPUT_FEATURE_TYPE_CHANGED',
  REQUIRED_INPUT_FEATURE_ADDED: 'MODELS.COMPATIBILITY_FINDING_REQUIRED_INPUT_FEATURE_ADDED',
  OPTIONAL_INPUT_FEATURE_ADDED: 'MODELS.COMPATIBILITY_FINDING_OPTIONAL_INPUT_FEATURE_ADDED',
  INPUT_FEATURE_DELETED: 'MODELS.COMPATIBILITY_FINDING_INPUT_FEATURE_DELETED',
  FEATURE_FORMULA_PARAMETERS_CHANGED: 'MODELS.COMPATIBILITY_FINDING_FEATURE_FORMULA_PARAMETERS_CHANGED',
  FEATURE_FORMULA_EXPRESSION_CHANGED: 'MODELS.COMPATIBILITY_FINDING_FEATURE_FORMULA_EXPRESSION_CHANGED',
} as const satisfies Record<string, string>;

/** Reason codes the client knows how to render. Derived from [REASON_CODE_KEYS]. */
export type KnownReasonCode = keyof typeof REASON_CODE_KEYS;

/**
 * Domain-level compatibility finding: the wire-level [ModelCompatibilityFindingOutputV1]
 * narrowed to a reasonCode the client knows how to render. Produced by
 * [isKnownModelCompatibilityFinding] as a filter predicate on the raw API response.
 */
export type KnownModelCompatibilityFinding = ModelCompatibilityFindingOutputV1 & { reasonCode: KnownReasonCode };

/** True when any finding has severity ERROR. */
export function hasBreakingFindings(findings: ModelCompatibilityFindingOutputV1[]): boolean {
  return findings.some((f) => f.severity === 'ERROR');
}

/** Maps a reasonCode to its i18n translation key. */
export function getReasonCodeTranslationKey(reasonCode: KnownReasonCode): string {
  return REASON_CODE_KEYS[reasonCode];
}

/**
 * Type guard that narrows `reasonCode` to the set of codes the client has i18n mappings for.
 * Safety net against a newer backend emitting a reason code the client's bundle predates —
 * such codes get filtered out rather than shown as raw translation keys.
 */
export function isKnownReasonCode(reasonCode: string): reasonCode is KnownReasonCode {
  return Object.hasOwn(REASON_CODE_KEYS, reasonCode);
}

/**
 * Type guard that promotes a wire-level finding to a [KnownModelCompatibilityFinding]
 * by asserting its reasonCode is one the client has an i18n mapping for.
 */
export function isKnownModelCompatibilityFinding(
  finding: ModelCompatibilityFindingOutputV1,
): finding is KnownModelCompatibilityFinding {
  return isKnownReasonCode(finding.reasonCode);
}

/**
 * Drops findings with unmapped reason codes and logs the codes.
 *
 * The filter keeps the frontend decoupled from the backend's check set: the server
 * can add new compatibility checks without a coordinated client release, and an
 * old client silently ignores codes it doesn't know. The tradeoff is under-reporting
 * breaking changes during client/server skew. If compatibility findings ever become
 * a hard gate for publish, revisit this — a shared DTO enum would turn drift into
 * a compile error instead of a silent drop.
 *
 * Apply at the data boundary (hooks/actions that fetch findings) so downstream UI
 * gating matches what `CompatibilityFindings` renders.
 */
export function filterKnownFindings(findings: ModelCompatibilityFindingOutputV1[]): KnownModelCompatibilityFinding[] {
  const known: KnownModelCompatibilityFinding[] = [];
  const dropped: string[] = [];
  for (const finding of findings) {
    if (isKnownModelCompatibilityFinding(finding)) {
      known.push(finding);
    } else {
      dropped.push(`${finding.reasonCode} (${finding.severity})`);
    }
  }
  if (dropped.length > 0) {
    // eslint-disable-next-line no-console
    console.error(`Models Compatibility Check: dropped unknown finding(s): ${dropped.join(', ')}`);
  }
  return known;
}

/** Maps a ChangeTypeEnum value (ADDED/REMOVED/RETAINED) to its i18n translation key. */
export function getChangeTypeTranslationKey(changeType: string): string {
  return `MODELS.COMPATIBILITY_CHANGE_TYPE_${changeType}`;
}

/** Maps canonicalId → feature name from the model's input and derived features. */
export function buildFeatureNameMap(
  inputFeatures: Array<{ canonicalId?: string; name: string }>,
  derivedFeatures: Array<{ canonicalId?: string; name: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const feature of [...inputFeatures, ...derivedFeatures]) {
    if (feature.canonicalId) {
      map.set(feature.canonicalId, feature.name);
    }
  }
  return map;
}

/** Builds a canonicalId → name map from a model version's input + derived features. */
export function buildNameMapFromModel(
  model:
    | {
        inputFeatures?: Array<{ canonicalId?: string; name?: string }>;
        derivedFeatures?: Array<{ canonicalId?: string; name?: string }>;
      }
    | undefined,
): Map<string, string> {
  const toNameEntry = (f: { canonicalId?: string; name?: string }) => ({
    canonicalId: f.canonicalId,
    name: f.name ?? '',
  });
  const inputs = (model?.inputFeatures ?? []).map(toNameEntry);
  const derived = (model?.derivedFeatures ?? []).map(toNameEntry);
  return buildFeatureNameMap(inputs, derived);
}
